import { useEffect, useRef, useState } from 'react'
import {
  Armchair,
  Boxes,
  Building2,
  ChevronDown,
  Contact,
  Gift,
  Landmark,
  Layers,
  Link2,
  Lock,
  LogOut,
  MapPin,
  Package,
  Scissors,
  ScrollText,
  Settings2,
  Upload,
  UserRound,
  Users,
  Wallet,
  Zap,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { OnlineBookingShareModal } from '@/components/booking/OnlineBookingShareDialog'
import { useAuth } from '@/contexts/AuthContext'
import { hasPermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

/**
 * @param {{ title: string, children: import('react').ReactNode }} props
 */
function MegaMenuSection({ title, children }) {
  return (
    <div className="min-w-0">
      <p className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

/**
 * @param {{
 *   icon: import('lucide-react').LucideIcon,
 *   label: string,
 *   onClick: () => void,
 *   destructive?: boolean,
 * }} props
 */
function MegaMenuItem({ icon: Icon, label, onClick, destructive = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] font-medium transition-colors',
        destructive ? 'text-destructive hover:bg-destructive/10' : 'text-foreground hover:bg-muted/80',
      )}
      onClick={onClick}
    >
      <Icon className={cn('h-3.5 w-3.5 shrink-0', destructive ? '' : 'text-muted-foreground')} />
      <span className="truncate">{label}</span>
    </button>
  )
}

export function UserMenu() {
  const navigate = useNavigate()
  const { user, isAuthenticated, lock, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const menuRef = useRef(null)

  const displayName = user?.name ?? 'Manager'
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const canAdmin = hasPermission(user, PERMISSIONS.ADMIN_FULL)
  const canUsers = hasPermission(user, PERMISSIONS.ADMIN_USERS)
  const canMasters = hasPermission(user, PERMISSIONS.MASTERS_MANAGE)
  const canAccounts = hasPermission(user, PERMISSIONS.ACCOUNTS_ACCESS)
  const canWorkspace = hasPermission(user, PERMISSIONS.STYLIST_WORKSPACE)
  const canHq = hasPermission(user, PERMISSIONS.HQ_VIEW)

  function closeMenu() {
    setOpen(false)
  }

  function go(path) {
    closeMenu()
    navigate(path)
  }

  useEffect(() => {
    if (!open) {
      return undefined
    }

    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const showAdmin = canAdmin || canUsers
  const showCatalog = canMasters
  const showFinance = canAccounts
  const showWorkspace = canWorkspace

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-2xl bg-muted/60 py-1 pl-1 pr-2.5 transition-colors hover:bg-muted"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-[hsl(262_70%_48%)] text-[10px] font-bold text-white">
          {initials}
        </span>
        <span className="hidden text-[12px] font-medium sm:block">{displayName.split(' ')[0]}</span>
        <ChevronDown className={cn('hidden h-3 w-3 text-muted-foreground transition-transform sm:block', open && 'rotate-180')} />
      </button>

      {open && isAuthenticated ? (
        <div
          role="menu"
          className={cn(
            'fixed z-[90] overflow-hidden rounded-2xl border border-border/60 bg-card shadow-2xl',
            'right-3 top-[4.25rem] w-[min(720px,calc(100vw-1.5rem))] max-h-[calc(100dvh-5.5rem)]',
          )}
        >
          <div className="max-h-[calc(100dvh-5.5rem)] overflow-y-auto p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <MegaMenuSection title="Quick">
                <MegaMenuItem
                  icon={Link2}
                  label="Share booking"
                  onClick={() => {
                    closeMenu()
                    setShareOpen(true)
                  }}
                />
                <MegaMenuItem icon={Lock} label="Lock screen" onClick={() => { closeMenu(); lock() }} />
              </MegaMenuSection>

              {showAdmin ? (
                <MegaMenuSection title="Administration">
                  {canAdmin ? (
                    <>
                      <MegaMenuItem icon={Building2} label="Company setup" onClick={() => go('/admin/company')} />
                      <MegaMenuItem icon={MapPin} label="Branch setup" onClick={() => go('/admin/branches')} />
                      <MegaMenuItem icon={ScrollText} label="Audit log" onClick={() => go('/admin/audit-logs')} />
                    </>
                  ) : null}
                  {canUsers ? (
                    <MegaMenuItem icon={Users} label="Users & roles" onClick={() => go('/admin/users')} />
                  ) : null}
                </MegaMenuSection>
              ) : null}

              {showCatalog ? (
                <MegaMenuSection title="Services & team">
                  <MegaMenuItem icon={Layers} label="Service categories" onClick={() => go('/masters/service-categories')} />
                  <MegaMenuItem icon={Scissors} label="Services" onClick={() => go('/masters/services')} />
                  <MegaMenuItem icon={UserRound} label="Stylists" onClick={() => go('/stylists')} />
                  <MegaMenuItem icon={Armchair} label="Chairs" onClick={() => go('/masters/chairs')} />
                  <MegaMenuItem icon={Contact} label="Customers" onClick={() => go('/customers')} />
                </MegaMenuSection>
              ) : null}

              {showCatalog ? (
                <MegaMenuSection title="Products & inventory">
                  <MegaMenuItem icon={Gift} label="Packages" onClick={() => go('/masters/packages')} />
                  <MegaMenuItem icon={Package} label="Products" onClick={() => go('/masters/products')} />
                  <MegaMenuItem icon={Boxes} label="Inventory" onClick={() => go('/inventory')} />
                </MegaMenuSection>
              ) : null}

              {showCatalog ? (
                <MegaMenuSection title="POS & catalog">
                  <MegaMenuItem icon={Zap} label="POS quick keys" onClick={() => go('/masters/pos-quick-keys')} />
                  <MegaMenuItem icon={Settings2} label="Catalog settings" onClick={() => go('/masters/catalog')} />
                  <MegaMenuItem icon={Upload} label="CSV import" onClick={() => go('/masters/import')} />
                </MegaMenuSection>
              ) : null}

              {showFinance ? (
                <MegaMenuSection title="Finance">
                  <MegaMenuItem icon={Landmark} label="Accounts" onClick={() => go('/accounts')} />
                </MegaMenuSection>
              ) : null}

              {canHq ? (
                <MegaMenuSection title="HQ">
                  <MegaMenuItem icon={Building2} label="HQ dashboard" onClick={() => go('/hq')} />
                </MegaMenuSection>
              ) : null}

              {showWorkspace ? (
                <MegaMenuSection title="Workspace">
                  <MegaMenuItem icon={Scissors} label="My day" onClick={() => go('/stylist')} />
                  <MegaMenuItem icon={Wallet} label="My commissions" onClick={() => go('/stylist?tab=commissions')} />
                </MegaMenuSection>
              ) : null}
            </div>
          </div>

          <div className="border-t border-border/60 bg-muted/20 px-4 py-2.5">
            <MegaMenuItem
              icon={LogOut}
              label="Sign out"
              destructive
              onClick={async () => {
                closeMenu()
                await logout()
                navigate('/login', { replace: true })
              }}
            />
          </div>
        </div>
      ) : null}

      <OnlineBookingShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  )
}
